<script setup lang="ts">
import { ArrowUpRight, Plus, RefreshCw } from '@lucide/vue'
import {
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    ZhEmptyState,
    ZhPageTitle,
    ZhSectionHeading,
    ZhStatusDot,
    ZhStatusPill,
    ZhTooltip,
} from '@zerohuman/ui'
import { computed, onMounted, ref } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { api, type MeEnterprise, type OrgMap } from '@/api'
import EnterpriseCreateForm from '@/components/EnterpriseCreateForm.vue'
import OrgMapCanvas from '@/components/OrgMap.vue'
import { reloadIntoEnterprise } from '@/lib/enterprisePicker'
import { buildOrgTree } from '@/lib/orgGraph'

const route = useRoute()
const router = useRouter()
const map = ref<OrgMap | null>(null)
/** Everything the person holds: with more than one, the map gains a level above the company. */
const enterprises = ref<MeEnterprise[]>([])
const loading = ref(true)
const error = ref<string | null>(null)
const tree = computed(() => (map.value ? buildOrgTree(map.value, enterprises.value) : null))
/**
 * The company stays the first thing seen even when it sits under a portfolio: the other enterprises are
 * one level up, where the trail and the back button lead.
 */
const homeId = computed(() => {
    const root = tree.value
    if (!root || root.kind !== 'portfolio') return
    return root.children.find(child => !child.switchTo)?.id
})
const centeredId = computed(() => (typeof route.query.circle === 'string' ? route.query.circle : homeId.value))
/** The New-enterprise dialog, opened from the header's + button. */
const creating = ref(false)
/** The enterprise a click offered to switch to, while the question is open. */
const switchTarget = ref<MeEnterprise | null>(null)
const switching = ref(false)
// Members with no team are drawn on the map beside the teams now, so only roles and tasks can be outside it.
const unplacedCount = computed(() => {
    const outside = map.value?.unplaced
    return outside ? outside.roles.length + outside.tasks.length : 0
})
const outsideGroups = computed(() => [
    { name: 'Roles', path: '/roles', items: map.value?.unplaced?.roles ?? [] },
    { name: 'Tasks', path: '/tasks', items: map.value?.unplaced?.tasks ?? [] },
])
/**
 * Why a role or task sits outside the map. `no_member` is a role's; the rest are a task's. A task belongs to a role,
 * not a team, so it is on the map once anyone holds its role, on any team or none.
 */
const REASONS = {
    no_member: 'Nobody holds this role',
    no_role: 'No role assigned',
    no_such_role: 'No role has that name',
    role_not_held: 'Nobody holds its role',
}
async function load(): Promise<void> {
    loading.value = true
    // The error stays up until a reload succeeds, so its Try again reads as disabled while the retry is in flight.
    // The loading surface stays hidden while it does, so the alert and a second status box never stack.
    try {
        // The person's other enterprises come with `me`; an older API, or a failed call, simply draws none.
        const [orgMap, me] = await Promise.all([api.orgMap(), api.me().catch(() => null)])
        map.value = orgMap
        enterprises.value = me?.enterprises ?? []
        error.value = null
    } catch {
        error.value = 'Could not load the map. Please try again.'
    } finally {
        loading.value = false
    }
}
onMounted(load)
function open(path: string): void {
    void router.push(path)
}
function rememberCircle(id: string): void {
    // The company is the map's home under a portfolio too, so it needs no remembering either.
    const home = id === tree.value?.id || id === homeId.value
    void router.replace({ query: { ...route.query, circle: home ? undefined : id } })
}
function offerSwitch(enterpriseId: string): void {
    switchTarget.value = enterprises.value.find(enterprise => enterprise.id === enterpriseId) ?? null
}
async function confirmSwitch(): Promise<void> {
    const target = switchTarget.value
    if (!target || switching.value) return
    switching.value = true
    error.value = null
    try {
        await api.selectEnterprise(target.id)
        reloadIntoEnterprise()
    } catch (caught) {
        error.value = caught instanceof Error ? `Could not switch (${caught.message}).` : 'Could not switch.'
        switchTarget.value = null
        switching.value = false
    }
}
</script>

<template>
    <section class="w-full space-y-4 p-4 md:p-8">
        <header class="flex items-center justify-between gap-4">
            <div><ZhPageTitle>Map</ZhPageTitle><p class="text-muted-foreground text-sm">{{ map?.enterprise.name ?? 'Your company' }} · Explore teams, people, roles and their tasks.</p></div>
            <!-- Two icons, named by their tooltips: the map is where a CEO starts, so a new company starts here too. -->
            <div class="flex shrink-0 items-center gap-2">
                <ZhTooltip text="New enterprise"><Button type="button" size="sm" variant="outline" aria-label="New enterprise" @click="creating = true"><Plus /></Button></ZhTooltip>
                <ZhTooltip text="Refresh map"><Button type="button" size="sm" variant="outline" :disabled="loading" aria-label="Refresh map" @click="load"><RefreshCw :class="{ 'animate-spin': loading }" /></Button></ZhTooltip>
            </div>
        </header>
        <Dialog :open="creating" @update:open="isOpen => { creating = isOpen }">
            <DialogContent data-testid="new-enterprise">
                <DialogHeader>
                    <DialogTitle>New enterprise</DialogTitle>
                    <DialogDescription>Another company under your name. The portal opens in it once it exists.</DialogDescription>
                </DialogHeader>
                <EnterpriseCreateForm @cancel="creating = false" />
            </DialogContent>
        </Dialog>
        <div v-if="error" class="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-destructive/30 bg-destructive/5 p-4" role="alert" data-testid="map-error"><p class="text-destructive text-sm">{{ error }}</p><Button type="button" variant="outline" size="sm" :disabled="loading" @click="load">Try again</Button></div>
        <ZhEmptyState v-if="loading && !tree && !error" label="Loading map" class="map-frame flex items-center justify-center" role="status" aria-label="Loading organization map"><ZhStatusDot state="active" class="mr-2" />Loading your organization…</ZhEmptyState>
        <div v-if="tree" class="map-frame" :class="{ 'map-frame-with-outside': unplacedCount }" :aria-busy="loading"><OrgMapCanvas :tree="tree" :centered-id="centeredId" @open="open" @update:centered-id="rememberCircle" @switch="offerSwitch" /></div>
        <Dialog :open="Boolean(switchTarget)" @update:open="isOpen => { if (!isOpen) switchTarget = null }">
            <DialogContent v-if="switchTarget" data-testid="switch-enterprise">
                <DialogHeader>
                    <DialogTitle>Switch to {{ switchTarget.name }}?</DialogTitle>
                    <DialogDescription>
                        You will leave {{ map?.enterprise.name ?? 'this enterprise' }} and open {{ switchTarget.name }}:
                        {{ switchTarget.memberCount }} {{ switchTarget.memberCount === 1 ? 'member' : 'members' }} in
                        {{ switchTarget.teamCount }} {{ switchTarget.teamCount === 1 ? 'team' : 'teams' }}. Every page reloads there.
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                    <Button type="button" variant="outline" :disabled="switching" @click="switchTarget = null">Stay here</Button>
                    <Button type="button" :disabled="switching" @click="confirmSwitch">Switch enterprise</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
        <details v-if="unplacedCount" class="rounded-xl border bg-card" data-testid="map-outside-structure">
            <summary class="cursor-pointer px-4 py-3 text-sm font-medium">Outside the team structure <ZhStatusPill tone="neutral" :count="unplacedCount" :pulse="false" class="ml-2" /></summary>
            <div class="space-y-4 border-t p-4">
                <p class="text-muted-foreground text-sm">These roles and tasks are part of the company, but do not have a complete place in the team map yet.</p>
                <div class="grid gap-5 md:grid-cols-2">
                    <section v-for="group in outsideGroups.filter(group => group.items.length)" :key="group.path" class="min-w-0 space-y-2">
                        <ZhSectionHeading tone="system">{{ group.name }} · {{ group.items.length }}</ZhSectionHeading>
                        <ul class="max-h-64 space-y-1 overflow-y-auto"><li v-for="item in group.items" :key="item.id"><RouterLink :to="`${group.path}/${item.id}`" class="flex items-center justify-between gap-3 rounded-lg px-2 py-2 hover:bg-muted"><span class="min-w-0"><span class="block break-words text-sm font-medium">{{ item.name }}</span><span class="text-xs text-muted-foreground">{{ REASONS[item.reason] }}</span></span><ArrowUpRight :size="14" class="shrink-0 text-muted-foreground" /></RouterLink></li></ul>
                    </section>
                </div>
            </div>
        </details>
    </section>
</template>

<style scoped>
.map-frame { height: calc(100dvh - 12rem); min-height: 520px; }
.map-frame-with-outside { height: calc(100dvh - 17rem); }
@media (max-width: 760px) { .map-frame { height: calc(100dvh - 11rem); min-height: 540px; } }
</style>
